import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type DragEvent,
} from "react";
import { toast } from "sonner";
import { formatDistanceToNow, parseISO } from "date-fns";
import {
  AlertCircle,
  Check,
  Download,
  File as FileIcon,
  FileSpreadsheet,
  FileText,
  Image as ImageIcon,
  Loader2,
  Paperclip,
  Presentation,
  RotateCw,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { cn } from "@/lib/utils";
import {
  ATTACHMENT_ALLOWED_MIMES,
  ATTACHMENT_MAX_BYTES,
  getDownloadUrl,
  validateAttachmentFiles,
  type AttachmentEntityType,
} from "@/api/attachments";
import type { AttachmentRead } from "@/api/types";

// ─── Thumbnail tunables ───────────────────────────────────────────────────
// Constants exposed at module level so the invariants are grep-verifiable
// (per the Phase C tests-as-invariants requirements). Don't change these
// without re-reading the performance bound in the Phase C spec.

/** Eagerly fetch signed URLs for at most this many image rows on
 *  mount when prefetchImages is true. The rest lazy-load through
 *  the IntersectionObserver. Hard-coded rather than computed via
 *  Math.min so a future "let's prefetch them all" temptation has
 *  to delete a constant, not nudge a number. */
const EAGER_PREFETCH_LIMIT = 10;

/** Refetch any cache entry within this many ms of expiring on the
 *  next visibilitychange to "visible". Keeps the user from seeing
 *  a broken image right after a long tab switch. */
const EXPIRY_REFETCH_THRESHOLD_MS = 60_000;

/** IntersectionObserver root margin — start fetching ~200px before
 *  a row scrolls into view so the image is already settled by the
 *  time it actually enters the viewport. */
const OBSERVER_ROOT_MARGIN = "200px 0px";

/** Strict allow list — explicit mime equality, NOT startsWith. A
 *  hypothetical future "image/svg+xml" or "image/webp" coming
 *  through the upload allow-list shouldn't silently start
 *  rendering as a thumbnail without a deliberate code change. */
function isImageMime(mime: string): boolean {
  return mime === "image/jpeg" || mime === "image/png";
}

/** Stable empty array reference for buffer-mode renders that
 *  still go through the eager-prefetch useMemo (the memo guards
 *  against the mode mismatch but still needs a deps slot). Using
 *  a module-level constant avoids a fresh `[]` on every render
 *  triggering a useless recompute. */
const EMPTY_ATTACHMENTS: AttachmentRead[] = [];

/** Per-row cache entry. The status field tracks the retry state
 *  so we don't loop on a sustained Supabase failure. */
interface ThumbnailCacheEntry {
  url: string;
  /** Epoch ms — pre-parsed once at write time so the hot path
   *  doesn't re-parse the ISO on every cache lookup. */
  expires_at_ms: number;
  status: "loaded" | "loading" | "failed-once" | "failed-final";
}

/** Entity context needed to resolve a thumbnail URL. Optional on
 *  AttachmentZone — without it, images render the doc-icon tile
 *  exactly like Phase A. */
export interface ThumbnailContext {
  slug: string;
  entityType: AttachmentEntityType;
  entityId: number;
}

/** Closure passed down to each row. The caller provides
 *  subscribe/unsubscribe (so AttachmentZone owns the single
 *  observer instance), cacheRef (so all rows share writes),
 *  resolveThumbnailUrl (one place that calls getDownloadUrl), and
 *  cacheBump (a counter the centralized visibilitychange handler
 *  increments to nudge visible rows into a re-evaluate). */
interface RowThumbnailContext {
  cacheRef: React.MutableRefObject<Map<string, ThumbnailCacheEntry>>;
  cacheBump: number;
  subscribe: (el: Element, onIntersect: (visible: boolean) => void) => void;
  unsubscribe: (el: Element) => void;
  resolveThumbnailUrl: (
    attachmentId: string,
    signal: AbortSignal,
  ) => Promise<{ url: string; expires_at_ms: number }>;
  trackController: (controller: AbortController) => void;
  releaseController: (controller: AbortController) => void;
}

// ─── File-type styling ────────────────────────────────────────────────────
// Static map keyed on the whitelisted mime types. Centralised so the icon
// row + the (future) richer preview share the same palette. Images get
// the same "doc tile" treatment as PDFs in Phase A — real thumbnails are
// Phase C work (signed-URL refetch loop, blob caching, etc.).
const FILE_STYLE: Record<
  string,
  { Icon: typeof FileText; bg: string; fg: string; label: string }
> = {
  "application/pdf": {
    Icon: FileText,
    bg: "bg-rose-50",
    fg: "text-rose-600",
    label: "PDF",
  },
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": {
    Icon: FileText,
    bg: "bg-blue-50",
    fg: "text-blue-600",
    label: "DOCX",
  },
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": {
    Icon: FileSpreadsheet,
    bg: "bg-emerald-50",
    fg: "text-emerald-600",
    label: "XLSX",
  },
  "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    {
      Icon: Presentation,
      bg: "bg-orange-50",
      fg: "text-orange-600",
      label: "PPTX",
    },
  "image/jpeg": {
    Icon: ImageIcon,
    bg: "bg-violet-50",
    fg: "text-violet-600",
    label: "JPG",
  },
  "image/png": {
    Icon: ImageIcon,
    bg: "bg-violet-50",
    fg: "text-violet-600",
    label: "PNG",
  },
};
const FALLBACK_STYLE = {
  Icon: FileIcon,
  bg: "bg-slate-100",
  fg: "text-slate-500",
  label: "FILE",
};

function styleFor(mime: string) {
  return FILE_STYLE[mime] ?? FALLBACK_STYLE;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const ACCEPT_ATTR = Array.from(ATTACHMENT_ALLOWED_MIMES).join(",");

// ─── Component ────────────────────────────────────────────────────────────

export type AttachmentZoneVariant = "compact" | "expanded";

interface LiveModeProps {
  mode: "live";
  attachments: AttachmentRead[];
  isLoading: boolean;
  /** Pre-fetched cap from useAttachments — passed in so the parent
   *  controls the source of truth (cap is derived from entityType
   *  upstream). */
  cap: number;
  /** Throws on failure — the component catches + toasts. */
  onUpload: (file: File) => Promise<AttachmentRead>;
  /** Throws on failure — the component catches + toasts. */
  onRemove: (id: string) => Promise<void>;
  /** Throws on failure — the component catches + toasts. */
  onDownload: (id: string) => Promise<void>;
  canUpload: boolean;
  /** Per-row delete gate — usually `uploader_user_id === me.id || isAdmin`. */
  canRemove: (att: AttachmentRead) => boolean;
  variant?: AttachmentZoneVariant;
  /** Optional header label override (e.g. "Files" vs "Attachments"). */
  title?: string;
  /** Entity context for the image thumbnail fetch pipeline. When
   *  omitted (Phase A/B callers that haven't migrated), image
   *  attachments fall through to the doc-icon tile path — the
   *  rest of the component is byte-for-byte identical to today. */
  imageThumbnails?: ThumbnailContext;
  /** When true AND `imageThumbnails` is provided, eagerly fetch
   *  signed URLs for the first EAGER_PREFETCH_LIMIT image rows on
   *  mount. Default false — rows still lazy-load via the
   *  IntersectionObserver, which is sufficient for low-cap
   *  surfaces like the Task modal. */
  prefetchImages?: boolean;
}

/** Per-row state for a buffered file — drives the inline upload
 *  indicator (spinner / check / error+retry) in Phase B's
 *  TaskCreateDialog. Index-aligned with `bufferedFiles`. */
export interface BufferedFileState {
  /** Local id (UUID) for keying; the parent uses it to update
   *  one row by identity without re-keying the whole array. */
  id: string;
  state: "idle" | "uploading" | "success" | "failed";
  /** Backend error message (or "Aborted") — surfaced as a tooltip
   *  on the error icon. */
  error?: string;
}

interface BufferModeProps {
  /**
   * Buffer mode — the entity ID doesn't exist yet (e.g. a not-
   * yet-created task in TaskCreateDialog), so files queue in
   * component-owned state and only upload after the parent
   * entity is created. Used in TaskCreateDialog's create flow.
   */
  mode: "buffer";
  cap: number;
  bufferedFiles: File[];
  onBufferAdd: (files: File[]) => void;
  onBufferRemove: (index: number) => void;
  canUpload: boolean;
  variant?: AttachmentZoneVariant;
  title?: string;
  /** Per-row state for the upload pipeline. When provided, rows
   *  show inline indicators (spinner / check / error). Absent →
   *  classic "queue with remove button" rendering, which is how
   *  buffer mode looks pre-Submit. Index-aligned with
   *  `bufferedFiles`. */
  bufferedFileStates?: BufferedFileState[];
  /** Retry one failed file by index. Only renders the retry
   *  button on rows whose state === "failed" AND when this prop
   *  is wired. */
  onRetry?: (index: number) => void;
}

export type AttachmentZoneProps = LiveModeProps | BufferModeProps;

/** Imperative handle so a parent (e.g. CompanyDetailPage's page-wide
 *  drag overlay) can route external file drops through the same
 *  validation pipeline this component already implements. Without
 *  this, the page would have to duplicate the validation logic. */
export interface AttachmentZoneHandle {
  /** Run the standard validate-then-upload chain on a list of files
   *  that came from anywhere (the zone's own drop, the input
   *  element, or a page-wide drag overlay). */
  handleFiles: (files: File[]) => Promise<void>;
}

export const AttachmentZone = forwardRef<
  AttachmentZoneHandle,
  AttachmentZoneProps
>(function AttachmentZone(props, ref) {
  const variant: AttachmentZoneVariant = props.variant ?? "expanded";
  const title = props.title ?? "Attachments";
  const confirm = useConfirm();
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Track per-attachment "currently deleting" state so we can dim
  // the row + disable its trash button. The signed-URL fetch is so
  // fast we don't bother per-row for downloads.
  const [removingIds, setRemovingIds] = useState<Set<string>>(() => new Set());
  const [uploadingCount, setUploadingCount] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  // Standard counter-based drag tracker — child elements fire
  // dragenter/leave as the pointer crosses them, which would otherwise
  // make the parent's "dragActive" state flicker.
  const dragCounterRef = useRef(0);

  // ─── Thumbnail pipeline ─────────────────────────────────────────────────
  // All of the following are no-ops when `imageThumbnails` is undefined —
  // Phase A/B callers don't pay for what they don't use.
  const imageThumbnails =
    props.mode === "live" ? props.imageThumbnails : undefined;
  const prefetchImages =
    props.mode === "live" ? !!props.prefetchImages : false;
  // Shared cache. useRef rather than useState — cache mutations
  // shouldn't fan out to every row. Each row owns its own local
  // status state; the cache is the cross-row authority.
  const thumbnailCacheRef = useRef<Map<string, ThumbnailCacheEntry>>(
    new Map(),
  );
  // Centralized IntersectionObserver (one per zone, NOT one per
  // row) plus a registry mapping target elements to their
  // per-row callbacks.
  const observerRef = useRef<IntersectionObserver | null>(null);
  const observerRegistryRef = useRef<
    Map<Element, (visible: boolean) => void>
  >(new Map());
  // Single Set of in-flight controllers — every thumbnail fetch
  // adds its controller here and removes it on settle, and the
  // zone aborts the lot on unmount. Belt-and-braces against a
  // stray fetch outliving the component.
  const pendingFetchControllersRef = useRef<Set<AbortController>>(new Set());
  // Bumped by the visibilitychange handler when expired entries
  // are evicted. Visible rows read it as an effect dep and
  // re-check the cache; if their entry is gone, they refetch.
  const [cacheBump, setCacheBump] = useState(0);

  // Resolve a signed thumbnail URL for one attachment, converting
  // the ISO `expires_at` to epoch ms at write time so the hot path
  // (cache lookup) stays cheap.
  const resolveThumbnailUrl = useCallback(
    async (attachmentId: string, signal: AbortSignal) => {
      if (!imageThumbnails) {
        throw new Error("resolveThumbnailUrl called without context");
      }
      const res = await getDownloadUrl(
        imageThumbnails.slug,
        imageThumbnails.entityType,
        imageThumbnails.entityId,
        attachmentId,
      );
      // axios honours the signal (passed via apiClient defaults
      // when supported; in our codebase getDownloadUrl doesn't
      // accept it explicitly, so the abort check below is the
      // safety net for any in-flight response we don't care about).
      if (signal.aborted) {
        throw new DOMException("Aborted", "AbortError");
      }
      const expires_at_ms = Date.parse(res.expires_at);
      return {
        url: res.url,
        // If the backend ever sends a malformed ISO, Date.parse
        // returns NaN → treat as immediately expired so the next
        // visit refetches rather than caching a permanently-
        // broken entry.
        expires_at_ms: Number.isFinite(expires_at_ms) ? expires_at_ms : 0,
      };
    },
    [imageThumbnails],
  );

  const trackController = useCallback((controller: AbortController) => {
    pendingFetchControllersRef.current.add(controller);
  }, []);
  const releaseController = useCallback((controller: AbortController) => {
    pendingFetchControllersRef.current.delete(controller);
  }, []);

  // Set up the IntersectionObserver once on mount. Tear down on
  // unmount AND abort any pending fetches so a closed dialog
  // can't keep nibbling at the network in the background.
  useEffect(() => {
    if (!imageThumbnails) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      // Defensive — SSR / older browsers shouldn't crash. Without
      // an observer, eager-prefetch rows still resolve; lazy rows
      // will appear immediately visible by definition (we'll
      // treat them as visible when the observer is unavailable).
      return undefined;
    }
    // Snapshot the ref Maps/Sets at effect-time. The useRef
    // instances themselves are stable across renders, but ESLint
    // can't prove that — capturing locals also keeps the
    // cleanup honest if a future refactor ever reassigns one.
    const registry = observerRegistryRef.current;
    const controllers = pendingFetchControllersRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const cb = registry.get(entry.target);
          if (cb) cb(entry.isIntersecting);
        }
      },
      { rootMargin: OBSERVER_ROOT_MARGIN, threshold: 0.01 },
    );
    observerRef.current = observer;
    return () => {
      observer.disconnect();
      observerRef.current = null;
      registry.clear();
      // Abort every in-flight fetch the zone's rows have started.
      for (const c of controllers) c.abort();
      controllers.clear();
    };
  }, [imageThumbnails]);

  const subscribeObserver = useCallback(
    (el: Element, onIntersect: (visible: boolean) => void) => {
      observerRegistryRef.current.set(el, onIntersect);
      const obs = observerRef.current;
      if (obs) {
        obs.observe(el);
      } else {
        // No observer instance (SSR / old browser) → treat the row
        // as immediately visible so its fetch proceeds.
        onIntersect(true);
      }
    },
    [],
  );
  const unsubscribeObserver = useCallback((el: Element) => {
    const obs = observerRef.current;
    if (obs) obs.unobserve(el);
    observerRegistryRef.current.delete(el);
  }, []);

  // Centralized visibilitychange listener — evicts cache entries
  // that have expired (or will within EXPIRY_REFETCH_THRESHOLD_MS)
  // and bumps the cache counter so visible rows refetch.
  useEffect(() => {
    if (!imageThumbnails) return undefined;
    const handler = () => {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      let anyEvicted = false;
      for (const [id, entry] of thumbnailCacheRef.current) {
        if (
          entry.status === "loaded" &&
          entry.expires_at_ms - now < EXPIRY_REFETCH_THRESHOLD_MS
        ) {
          thumbnailCacheRef.current.delete(id);
          anyEvicted = true;
        }
      }
      if (anyEvicted) setCacheBump((n) => n + 1);
    };
    document.addEventListener("visibilitychange", handler);
    return () => {
      document.removeEventListener("visibilitychange", handler);
    };
  }, [imageThumbnails]);

  // The Set of attachment ids eligible for eager prefetch — the
  // first EAGER_PREFETCH_LIMIT image rows, in list order. Memo'd
  // off the attachments array so a re-render with the same list
  // doesn't re-trigger the eager path.
  const liveAttachmentsForMemo: AttachmentRead[] =
    props.mode === "live" ? props.attachments : EMPTY_ATTACHMENTS;
  const eagerPrefetchIds = useMemo<Set<string>>(() => {
    if (!imageThumbnails || !prefetchImages) return new Set();
    const out = new Set<string>();
    let taken = 0;
    for (const att of liveAttachmentsForMemo) {
      if (taken >= EAGER_PREFETCH_LIMIT) break;
      if (isImageMime(att.mime_type)) {
        out.add(att.id);
        taken += 1;
      }
    }
    return out;
  }, [imageThumbnails, prefetchImages, liveAttachmentsForMemo]);

  // Single object reference passed down to every image row. Memo
  // so children's effect deps stay stable across re-renders.
  const rowThumbnailContext = useMemo<RowThumbnailContext | null>(() => {
    if (!imageThumbnails) return null;
    return {
      cacheRef: thumbnailCacheRef,
      cacheBump,
      subscribe: subscribeObserver,
      unsubscribe: unsubscribeObserver,
      resolveThumbnailUrl,
      trackController,
      releaseController,
    };
  }, [
    imageThumbnails,
    cacheBump,
    subscribeObserver,
    unsubscribeObserver,
    resolveThumbnailUrl,
    trackController,
    releaseController,
  ]);

  const currentCount =
    props.mode === "live"
      ? props.attachments.length
      : props.bufferedFiles.length;
  const cap = props.cap;
  const capRemaining = Math.max(0, cap - currentCount);
  const atCap = capRemaining === 0;

  /** The one-and-only validation + upload pipeline. Used by:
   *   - The hidden <input>'s onChange
   *   - The zone's own drop handler
   *   - The imperative `handleFiles` exposed via ref (page-wide drop) */
  const handleFiles = useCallback(
    async (rawFiles: File[]) => {
      if (!props.canUpload) {
        toast.error("You don't have permission to upload attachments here.");
        return;
      }
      if (rawFiles.length === 0) return;

      const { accepted, rejected, capHit } = validateAttachmentFiles(rawFiles, {
        existingCount: currentCount,
        cap,
      });

      // Group rejection toasts so a bulk drop with mixed errors
      // doesn't fire one toast per file (which would overflow).
      const mimeRejects = rejected.filter((r) => r.reason === "mime");
      const sizeRejects = rejected.filter((r) => r.reason === "size");
      if (mimeRejects.length > 0) {
        toast.error(
          mimeRejects.length === 1
            ? `"${mimeRejects[0].file.name}" is not an allowed file type.`
            : `${mimeRejects.length} files skipped, unsupported types.`,
        );
      }
      if (sizeRejects.length > 0) {
        toast.error(
          sizeRejects.length === 1
            ? `"${sizeRejects[0].file.name}" exceeds ${formatBytes(ATTACHMENT_MAX_BYTES)}.`
            : `${sizeRejects.length} files skipped, over ${formatBytes(ATTACHMENT_MAX_BYTES)}.`,
        );
      }
      if (capHit) {
        toast.error(
          `This ${variantLabel(props)} already has ${currentCount} / ${cap} attachments, extra files were skipped.`,
        );
      }
      if (accepted.length === 0) return;

      if (props.mode === "buffer") {
        props.onBufferAdd(accepted);
        return;
      }

      // Live mode — fire uploads sequentially. The server enforces
      // its own cap on each call, so a race with another tab where
      // we'd push past the cap is rejected server-side and surfaced
      // here as a toast.
      setUploadingCount(accepted.length);
      let succeeded = 0;
      try {
        for (const file of accepted) {
          try {
            await props.onUpload(file);
            succeeded += 1;
          } catch (err) {
            const message = extractAxiosDetail(err);
            toast.error(
              message ?? `Couldn't upload "${file.name}".`,
            );
          }
        }
        if (succeeded > 0) {
          toast.success(
            succeeded === 1
              ? "Attachment uploaded."
              : `${succeeded} attachments uploaded.`,
          );
        }
      } finally {
        setUploadingCount(0);
      }
    },
    [props, currentCount, cap],
  );

  useImperativeHandle(ref, () => ({ handleFiles }), [handleFiles]);

  const onDropZone = useCallback(
    async (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      dragCounterRef.current = 0;
      setDragActive(false);
      const files = Array.from(e.dataTransfer?.files ?? []);
      await handleFiles(files);
    },
    [handleFiles],
  );

  const onDragEnterZone = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragCounterRef.current += 1;
    if (dragCounterRef.current === 1) setDragActive(true);
  };
  const onDragLeaveZone = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) setDragActive(false);
  };
  const onDragOverZone = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const openPicker = () => {
    if (!props.canUpload || atCap) return;
    inputRef.current?.click();
  };

  const handleRemoveLive = async (att: AttachmentRead) => {
    if (props.mode !== "live") return;
    const confirmed = await confirm({
      title: "Delete attachment?",
      description: `"${att.filename}" will be removed. This can't be undone.`,
      confirmLabel: "Delete",
      variant: "danger",
    });
    if (!confirmed) return;
    setRemovingIds((prev) => {
      const next = new Set(prev);
      next.add(att.id);
      return next;
    });
    try {
      await props.onRemove(att.id);
      toast.success("Attachment removed.");
    } catch (err) {
      toast.error(extractAxiosDetail(err) ?? "Couldn't remove attachment.");
    } finally {
      setRemovingIds((prev) => {
        const next = new Set(prev);
        next.delete(att.id);
        return next;
      });
    }
  };

  const handleDownloadLive = async (att: AttachmentRead) => {
    if (props.mode !== "live") return;
    try {
      await props.onDownload(att.id);
    } catch (err) {
      toast.error(extractAxiosDetail(err) ?? "Couldn't open download.");
    }
  };

  const isLoading = props.mode === "live" && props.isLoading;
  const showEmpty =
    !isLoading && currentCount === 0 && uploadingCount === 0;

  return (
    <div
      className={cn(
        "rounded-plane bg-white shadow-plane",
        variant === "compact" ? "p-3" : "p-5",
      )}
    >
      {/* Header */}
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Paperclip className="h-4 w-4 text-slate-400" />
          <h3
            className={cn(
              "font-medium text-slate-700",
              variant === "compact" ? "text-sm" : "text-sm",
            )}
          >
            {title}
          </h3>
          <span className="text-xs tabular-nums text-slate-400">
            {currentCount} / {cap}
          </span>
        </div>
        {props.canUpload && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={atCap || uploadingCount > 0}
            onClick={openPicker}
            className="h-7 text-xs"
          >
            <Upload className="mr-1.5 h-3.5 w-3.5" />
            Upload
          </Button>
        )}
      </div>

      {/* Drop zone — always rendered when the caller can upload. The
          variant just shrinks the padding. We keep the zone visible
          even when at-cap so the user gets the disabled message in
          place rather than a vanishing UI. */}
      {props.canUpload && (
        <div
          onDrop={onDropZone}
          onDragEnter={onDragEnterZone}
          onDragLeave={onDragLeaveZone}
          onDragOver={onDragOverZone}
          onClick={openPicker}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              openPicker();
            }
          }}
          className={cn(
            "flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed text-center transition-colors",
            variant === "compact" ? "py-3 text-xs" : "py-6 text-sm",
            dragActive
              ? "border-indigo-400 bg-indigo-50 text-indigo-700"
              : "border-slate-200 text-slate-500 hover:bg-slate-50",
            atCap && "cursor-not-allowed opacity-60 hover:bg-transparent",
          )}
        >
          <Upload
            className={cn(
              "mb-1 text-slate-400",
              variant === "compact" ? "h-4 w-4" : "h-5 w-5",
              dragActive && "text-indigo-500",
            )}
          />
          {atCap ? (
            <span>Cap reached ({cap} files max).</span>
          ) : (
            <>
              <span>
                {dragActive
                  ? "Drop to upload"
                  : "Drop files here or click to browse"}
              </span>
              {variant === "expanded" && (
                <span className="mt-0.5 text-xs text-slate-400">
                  PDF, DOCX, XLSX, PPTX, JPG, PNG · up to{" "}
                  {formatBytes(ATTACHMENT_MAX_BYTES)}
                </span>
              )}
            </>
          )}
        </div>
      )}

      {/* Hidden picker. accept= is a hint to the OS dialog only —
          the real gate is validateAttachmentFiles. */}
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPT_ATTR}
        className="hidden"
        onChange={async (e) => {
          const files = Array.from(e.target.files ?? []);
          // Reset the input value so the user can re-pick the same
          // file (after a failure, say) — without this, onChange
          // wouldn't fire for an identical second selection.
          e.target.value = "";
          await handleFiles(files);
        }}
      />

      {/* File list. Hidden in pure "no files + no uploads" mode for
          a tidier compact variant — the empty-state copy already
          lives inside the drop zone above. */}
      {props.mode === "live" ? (
        <LiveList
          attachments={props.attachments}
          uploadingCount={uploadingCount}
          isLoading={isLoading}
          showEmpty={showEmpty && variant === "expanded"}
          variant={variant}
          removingIds={removingIds}
          canRemove={props.canRemove}
          onDownload={handleDownloadLive}
          onRemove={handleRemoveLive}
          thumbnailContext={rowThumbnailContext}
          eagerPrefetchIds={eagerPrefetchIds}
        />
      ) : (
        <BufferList
          files={props.bufferedFiles}
          states={props.bufferedFileStates}
          onRetry={props.onRetry}
          variant={variant}
          showEmpty={showEmpty && variant === "expanded"}
          onRemove={props.onBufferRemove}
        />
      )}
    </div>
  );
});

// ─── Sub-renderers ────────────────────────────────────────────────────────

function LiveList({
  attachments,
  uploadingCount,
  isLoading,
  showEmpty,
  variant,
  removingIds,
  canRemove,
  onDownload,
  onRemove,
  thumbnailContext,
  eagerPrefetchIds,
}: {
  attachments: AttachmentRead[];
  uploadingCount: number;
  isLoading: boolean;
  showEmpty: boolean;
  variant: AttachmentZoneVariant;
  removingIds: Set<string>;
  canRemove: (att: AttachmentRead) => boolean;
  onDownload: (att: AttachmentRead) => void;
  onRemove: (att: AttachmentRead) => void;
  thumbnailContext: RowThumbnailContext | null;
  eagerPrefetchIds: Set<string>;
}) {
  if (isLoading && attachments.length === 0) {
    return (
      <div className="mt-3 flex items-center gap-2 text-xs text-slate-400">
        <Loader2 className="h-3 w-3 animate-spin" />
        Loading attachments…
      </div>
    );
  }
  if (showEmpty) {
    return null;
  }
  return (
    <ul className={cn("mt-3 space-y-1.5", variant === "compact" && "space-y-1")}>
      {attachments.map((att) => {
        // Image rows render a self-managed ImageThumbnail tile when
        // the thumbnail context is wired; otherwise (Phase A/B
        // callers without imageThumbnails, OR any future mime
        // outside the strict allow list) they fall through to
        // FileRow's default doc-icon path.
        const tile =
          thumbnailContext && isImageMime(att.mime_type) ? (
            <ImageThumbnail
              attachmentId={att.id}
              filename={att.filename}
              mime={att.mime_type}
              variant={variant}
              ctx={thumbnailContext}
              eagerPrefetch={eagerPrefetchIds.has(att.id)}
            />
          ) : undefined;
        return (
          <FileRow
            key={att.id}
            filename={att.filename}
            mime={att.mime_type}
            size={att.size_bytes}
            uploaderName={att.uploader_name}
            createdAt={att.created_at}
            variant={variant}
            isRemoving={removingIds.has(att.id)}
            canRemove={canRemove(att)}
            onDownload={() => onDownload(att)}
            onRemove={() => onRemove(att)}
            tile={tile}
          />
        );
      })}
      {uploadingCount > 0 && (
        <li className="flex items-center gap-2 rounded-lg border border-dashed border-slate-200 px-3 py-2 text-xs text-slate-500">
          <Loader2 className="h-3 w-3 animate-spin" />
          Uploading {uploadingCount} file{uploadingCount === 1 ? "" : "s"}…
        </li>
      )}
    </ul>
  );
}

function BufferList({
  files,
  states,
  onRetry,
  variant,
  showEmpty,
  onRemove,
}: {
  files: File[];
  states: BufferedFileState[] | undefined;
  onRetry: ((index: number) => void) | undefined;
  variant: AttachmentZoneVariant;
  showEmpty: boolean;
  onRemove: (index: number) => void;
}) {
  if (showEmpty) return null;
  return (
    <ul className={cn("mt-3 space-y-1.5", variant === "compact" && "space-y-1")}>
      {files.map((f, i) => {
        // The states array is index-aligned with files; defaulting
        // missing entries to "idle" keeps Phase A's caller
        // (no states array) rendering as before.
        const rowState = states?.[i]?.state ?? "idle";
        const rowError = states?.[i]?.error;
        // Don't let the user delete a row that's currently
        // uploading — the underlying request would still finish
        // and create an orphan attachment. Successful + failed
        // rows are removable: success removes from the buffer
        // UI (the server row stays), failed removes the doomed
        // local entry.
        const allowRemove = rowState !== "uploading";
        return (
          <FileRow
            // Prefer the stable local id when available — survives
            // re-orderings without re-mounting the row. Falls
            // back to the (name, index) tuple Phase A used.
            key={states?.[i]?.id ?? `${f.name}-${i}`}
            filename={f.name}
            mime={f.type}
            size={f.size}
            uploaderName={null}
            createdAt={null}
            variant={variant}
            isRemoving={false}
            canRemove={allowRemove}
            onRemove={() => onRemove(i)}
            bufferState={rowState}
            bufferError={rowError}
            onRetry={
              rowState === "failed" && onRetry
                ? () => onRetry(i)
                : undefined
            }
          />
        );
      })}
    </ul>
  );
}

function FileRow({
  filename,
  mime,
  size,
  uploaderName,
  createdAt,
  variant,
  isRemoving,
  canRemove,
  onDownload,
  onRemove,
  bufferState,
  bufferError,
  onRetry,
  tile,
}: {
  filename: string;
  mime: string;
  size: number;
  uploaderName: string | null;
  createdAt: string | null;
  variant: AttachmentZoneVariant;
  isRemoving: boolean;
  canRemove: boolean;
  onDownload?: () => void;
  onRemove: () => void;
  /** Per-row upload state for buffer mode. Undefined → no
   *  indicator (Phase A's classic queue-with-remove rendering). */
  bufferState?: BufferedFileState["state"];
  bufferError?: string;
  /** Wired only on `failed` rows when the parent supports retry. */
  onRetry?: () => void;
  /** Optional drop-in for the tile slot. When provided, replaces
   *  the default doc-icon tile entirely. Used by LiveList to
   *  render image thumbnails for jpeg/png attachments — the
   *  component owns its own dimensions matching the doc-icon
   *  tile (compact: h-7 w-7, expanded: h-9 w-9). */
  tile?: React.ReactNode;
}) {
  const style = styleFor(mime);
  const Icon = style.Icon;
  const meta = [formatBytes(size)];
  if (uploaderName) meta.push(uploaderName);
  if (createdAt) {
    try {
      meta.push(formatDistanceToNow(parseISO(createdAt), { addSuffix: true }));
    } catch {
      // Bad ISO from the server is treated as "no date" — better a
      // silent fallback than a thrown render.
    }
  }
  // The error string is also surfaced in the metadata line so the
  // user reads the cause without having to hover the icon (the
  // tooltip-as-only-channel is a usability anti-pattern for forms).
  if (bufferState === "failed" && bufferError) {
    meta.push(bufferError);
  }

  return (
    <li
      className={cn(
        "group flex items-center gap-3 rounded-lg border border-slate-200/80 bg-white p-2 transition-colors duration-150 hover:bg-indigo-500/[0.04]",
        isRemoving && "pointer-events-none opacity-50",
        variant === "compact" && "p-1.5",
        bufferState === "failed" && "border-rose-200 bg-rose-50/40",
      )}
    >
      {tile ?? (
        <div
          className={cn(
            "flex shrink-0 items-center justify-center rounded-md",
            style.bg,
            style.fg,
            variant === "compact" ? "h-7 w-7" : "h-9 w-9",
          )}
          aria-hidden
        >
          <Icon
            className={variant === "compact" ? "h-3.5 w-3.5" : "h-4 w-4"}
          />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p
          className={cn(
            "truncate text-slate-800",
            variant === "compact" ? "text-xs" : "text-sm",
          )}
          title={filename}
        >
          {filename}
        </p>
        <p
          className={cn(
            "truncate",
            variant === "compact" ? "text-[10px]" : "text-xs",
            bufferState === "failed" ? "text-rose-600" : "text-slate-400",
          )}
        >
          {meta.join(" · ")}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {/* Per-row state indicator — only renders in buffer mode
         *  when the parent ships a states array. Uses a fixed
         *  width so success/failed/uploading rows align with the
         *  idle ones. */}
        {bufferState === "uploading" && (
          <span
            className="flex h-7 w-7 items-center justify-center text-indigo-500"
            aria-label="Uploading"
            title="Uploading…"
          >
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          </span>
        )}
        {bufferState === "success" && (
          <span
            className="flex h-7 w-7 items-center justify-center text-emerald-500"
            aria-label="Uploaded"
            title="Uploaded"
          >
            <Check className="h-3.5 w-3.5" />
          </span>
        )}
        {bufferState === "failed" && (
          <>
            <span
              className="flex h-7 w-7 items-center justify-center text-rose-500"
              aria-label="Upload failed"
              title={bufferError ?? "Upload failed"}
            >
              <AlertCircle className="h-3.5 w-3.5" />
            </span>
            {onRetry && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 text-slate-500 hover:text-indigo-600"
                onClick={onRetry}
                aria-label="Retry upload"
                title="Retry upload"
              >
                <RotateCw className="h-3.5 w-3.5" />
              </Button>
            )}
          </>
        )}
        <div
          className={cn(
            "flex items-center gap-1",
            // In buffer mode without active state machinery the
            // remove button only fades in on hover (Phase A
            // styling). Once a state machine is wired, keep the
            // affordance always visible so a user with a failed
            // row can clearly discard or retry.
            bufferState === undefined &&
              "opacity-60 transition-opacity group-hover:opacity-100",
          )}
        >
          {onDownload && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={onDownload}
              disabled={isRemoving}
              aria-label="Download"
              title="Download"
            >
              <Download className="h-3.5 w-3.5" />
            </Button>
          )}
          {canRemove && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0 text-slate-400 hover:text-rose-600"
              onClick={onRemove}
              disabled={isRemoving}
              aria-label={onDownload ? "Delete" : "Remove"}
              title={onDownload ? "Delete" : "Remove"}
            >
              {onDownload ? (
                <Trash2 className="h-3.5 w-3.5" />
              ) : (
                <X className="h-3.5 w-3.5" />
              )}
            </Button>
          )}
        </div>
      </div>
    </li>
  );
}

// ─── Image thumbnail tile ────────────────────────────────────────────────
// One instance per image row. Owns its own local state (loading /
// loaded / failed) so a re-resolve elsewhere doesn't churn the entire
// list. The shared cache lives in the parent AttachmentZone — this
// component only reads / writes it through the ctx.

function ImageThumbnail({
  attachmentId,
  filename,
  mime,
  variant,
  ctx,
  eagerPrefetch,
}: {
  attachmentId: string;
  filename: string;
  mime: string;
  variant: AttachmentZoneVariant;
  ctx: RowThumbnailContext;
  eagerPrefetch: boolean;
}) {
  // Tracks what THIS row currently shows. Independent from the
  // shared cache — the cache evicts on expiry but we want the
  // visible image to stay put while the refetch is in flight (a
  // brief flash to skeleton on every visibility change would be
  // worse UX than holding the stale image one extra second).
  type RowStatus = "idle" | "loading" | "loaded" | "failed";
  const [status, setStatus] = useState<RowStatus>("idle");
  const [url, setUrl] = useState<string | null>(null);
  // Visibility ratchet — flips to true the first time the row
  // crosses the observer's threshold; never flips back. Eager-
  // prefetch rows skip this and fetch immediately.
  const [isVisible, setIsVisible] = useState(false);

  const tileRef = useRef<HTMLDivElement | null>(null);
  const fallbackStyle = styleFor(mime);
  const FallbackIcon = fallbackStyle.Icon;

  // Subscribe to the central observer. Eager-prefetch rows can
  // skip this — they fetch on mount regardless.
  useEffect(() => {
    if (eagerPrefetch) {
      setIsVisible(true);
      return undefined;
    }
    const el = tileRef.current;
    if (!el) return undefined;
    ctx.subscribe(el, (visible) => {
      if (visible) setIsVisible(true);
    });
    return () => {
      ctx.unsubscribe(el);
    };
    // ctx is a memoized object reference; subscribe / unsubscribe
    // are stable useCallbacks. Re-subscribing on every cacheBump
    // would needlessly re-observe — exclude it from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eagerPrefetch]);

  // Drive the actual fetch. Re-runs when visibility flips OR the
  // cache gets bumped (post-evict on tab refocus). Each invocation
  // checks the cache first; if a fresh entry exists, it shortcuts
  // to "loaded" without firing a request.
  useEffect(() => {
    if (!isVisible && !eagerPrefetch) return undefined;

    const cached = ctx.cacheRef.current.get(attachmentId);
    const now = Date.now();

    if (
      cached &&
      cached.status === "loaded" &&
      cached.expires_at_ms - now > EXPIRY_REFETCH_THRESHOLD_MS
    ) {
      setUrl(cached.url);
      setStatus("loaded");
      return undefined;
    }
    if (cached && cached.status === "failed-final") {
      // Already tried twice and gave up — render fallback, don't
      // keep poking the network on every visibility flip.
      setStatus("failed");
      return undefined;
    }

    // No usable cache → fetch. Mark as 'loading' in the cache so
    // a sibling row mounting for the same attachment id (unlikely
    // but cheap insurance) doesn't double-fetch.
    ctx.cacheRef.current.set(attachmentId, {
      url: "",
      expires_at_ms: 0,
      status: "loading",
    });
    if (status !== "loaded") setStatus("loading");

    const controller = new AbortController();
    ctx.trackController(controller);

    let cancelled = false;
    (async () => {
      try {
        const res = await ctx.resolveThumbnailUrl(
          attachmentId,
          controller.signal,
        );
        if (cancelled || controller.signal.aborted) return;
        ctx.cacheRef.current.set(attachmentId, {
          url: res.url,
          expires_at_ms: res.expires_at_ms,
          status: "loaded",
        });
        setUrl(res.url);
        setStatus("loaded");
      } catch {
        if (cancelled || controller.signal.aborted) return;
        // Surface as failed-once → the per-<img> onError below
        // will promote to failed-final after the second strike.
        ctx.cacheRef.current.set(attachmentId, {
          url: "",
          expires_at_ms: 0,
          status: "failed-once",
        });
        setStatus("failed");
      } finally {
        ctx.releaseController(controller);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
      ctx.releaseController(controller);
    };
    // status is intentionally omitted — including it would re-run
    // the effect on the state transitions WE drive inside the
    // effect, creating a loop. The cache-bump dep handles the
    // legitimate re-evaluate case.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachmentId, isVisible, eagerPrefetch, ctx.cacheBump]);

  /** <img onError> handler — runs when the browser can't load the
   *  signed URL (network failure or storage 4xx). Promotes the
   *  cache entry's retry counter: first failure → ONE refetch;
   *  second failure → permanent fallback. */
  const handleImgError = useCallback(() => {
    const cached = ctx.cacheRef.current.get(attachmentId);
    if (cached?.status === "failed-once") {
      ctx.cacheRef.current.set(attachmentId, {
        ...cached,
        status: "failed-final",
      });
      setStatus("failed");
      return;
    }
    // Mark as failed-once and refetch a fresh signed URL — the
    // existing one may simply have expired between mount and
    // <img> load.
    ctx.cacheRef.current.set(attachmentId, {
      url: "",
      expires_at_ms: 0,
      status: "failed-once",
    });
    setStatus("loading");
    setUrl(null);

    const controller = new AbortController();
    ctx.trackController(controller);
    (async () => {
      try {
        const res = await ctx.resolveThumbnailUrl(
          attachmentId,
          controller.signal,
        );
        if (controller.signal.aborted) return;
        ctx.cacheRef.current.set(attachmentId, {
          url: res.url,
          expires_at_ms: res.expires_at_ms,
          status: "loaded",
        });
        setUrl(res.url);
        setStatus("loaded");
      } catch {
        if (controller.signal.aborted) return;
        ctx.cacheRef.current.set(attachmentId, {
          url: "",
          expires_at_ms: 0,
          status: "failed-final",
        });
        setStatus("failed");
      } finally {
        ctx.releaseController(controller);
      }
    })();
  }, [attachmentId, ctx]);

  // Three rendering branches — all using the SAME tile geometry
  // as FileRow's default doc-icon path so non-image rows in the
  // same list align pixel-for-pixel.
  const tileClass = cn(
    "flex shrink-0 items-center justify-center overflow-hidden rounded-md",
    variant === "compact" ? "h-7 w-7" : "h-9 w-9",
  );

  if (status === "failed") {
    // Doc-icon fallback — identical to the path FileRow renders
    // for non-image attachments. We render it inline rather than
    // recursing into FileRow to keep the rendering boundary
    // simple.
    return (
      <div
        ref={tileRef}
        className={cn(tileClass, fallbackStyle.bg, fallbackStyle.fg)}
        aria-hidden
        title={filename}
      >
        <FallbackIcon
          className={variant === "compact" ? "h-3.5 w-3.5" : "h-4 w-4"}
        />
      </div>
    );
  }

  if (status === "loaded" && url) {
    return (
      <div ref={tileRef} className={tileClass}>
        <img
          src={url}
          alt={filename}
          loading="lazy"
          onError={handleImgError}
          className="h-full w-full object-cover"
        />
      </div>
    );
  }

  // 'idle' (waiting for visibility) and 'loading' both render the
  // skeleton. Idle rows would otherwise be invisible holes in the
  // layout until the observer fires.
  return (
    <div
      ref={tileRef}
      className={cn(tileClass, "animate-pulse bg-slate-200")}
      aria-label="Loading thumbnail"
    />
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function variantLabel(props: AttachmentZoneProps): string {
  if (props.mode === "live") {
    // We don't carry the entity name through — "item" is the
    // closest non-confusing copy. Most callers wrap the zone in a
    // card with a clear entity label above.
    return "item";
  }
  return "item";
}

function extractAxiosDetail(err: unknown): string | null {
  if (
    err &&
    typeof err === "object" &&
    "response" in err &&
    (err as { response?: unknown }).response &&
    typeof (err as { response?: { data?: unknown } }).response === "object"
  ) {
    const data = (err as { response?: { data?: unknown } }).response?.data;
    if (data && typeof data === "object" && "detail" in data) {
      const detail = (data as { detail?: unknown }).detail;
      if (typeof detail === "string") return detail;
    }
  }
  if (err instanceof Error) return err.message;
  return null;
}
